import React, {Component} from 'react';
import {APIClient} from '../../../utils/APIClient';
import {API_URL, TIKTOK_APP_ID, APP_URL, LINKEDIN_AUTHORIZATION_URL} from '../../../settings/local_settings';
import {YesNoModal} from "../../elements/YesNoModal";
import {FaIcon} from "../../FaIcon";
import {Spinner} from "../../Spinner";
import {toast} from "react-toastify";
import { withTranslation } from 'react-i18next';
import {ManagedByClientTag} from '../../client/ad_accounts/ManagedByClientTag';
import {setPendingLinkedinReconnect} from './pendingLinkedinReconnect';
import {rememberCredentialReconnect} from './reconnectCredential';
import {escalafyConnectErrorKey, startEscalafyAgencyOAuth} from './EscalafyConnectButton';
import {startMercadoAdsAgencyAuth} from '../../channels/mercado_ads/mercadoAdsApi';
import {errorViewOf, mercadoAdsConnectionName} from '../../channels/mercado_ads/mercadoAds';

const STATUS_DISCONNECTED = 'DISCONNECTED';
const ESCALAFY = 'ESCALAFY';
const MERCADO_ADS = 'MERCADO_ADS';
// `#4B5563`: el gris de las otras metas (`#6B7280`) queda en 3,8:1 sobre la fila rayada (#E4E4E4); éste, ~6:1.
const META_STYLE = { marginTop: '0.25em', fontSize: '0.8125rem', color: '#4B5563' };
// LinkedIn corta el acceso a los 60 días (card fqyBbFvp): desde una semana antes la fila avisa y
// ofrece Reconectar, igual que el mail que manda el backend.
const EXPIRY_WARNING_MS = 7 * 24 * 60 * 60 * 1000;
// Colores del texto de vencimiento, oscurecidos para llegar a AA (4,5:1) también sobre la fila rayada
// (#E4E4E4): «Vence» es una advertencia (ámbar); «Venció» ya no trae datos, es un fallo (rojo).
const EXPIRING_COLOR = '#92400E';
const EXPIRED_COLOR = '#B91C1C';


class CredentialRowClass extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isReconnecting: false,
            isEditing: false,
            editValue: '',
            isSavingLabel: false,
        };
        this.handleRemove = this.handleRemove.bind(this);
        this.handleReconnect = this.handleReconnect.bind(this);
        this.handleStartEdit = this.handleStartEdit.bind(this);
        this.handleCancelEdit = this.handleCancelEdit.bind(this);
        this.handleSaveLabel = this.handleSaveLabel.bind(this);
        this.handleEditKeyDown = this.handleEditKeyDown.bind(this);
    }

    handleStartEdit() {
        const { credential } = this.props;
        this.setState({ isEditing: true, editValue: credential.label || '' });
    }

    handleCancelEdit() {
        this.setState({ isEditing: false, editValue: '', isSavingLabel: false });
    }

    handleSaveLabel() {
        const { credential, channel, t } = this.props;
        const { editValue, isSavingLabel } = this.state;
        if (isSavingLabel) return;

        this.setState({ isSavingLabel: true });
        new APIClient(`${API_URL}agency/credentials/${channel}/${credential.id}/`).call('PATCH', {
            label: editValue,
        }).then((data) => {
            if (data && data.status === 'OK') {
                this.setState({ isEditing: false, editValue: '', isSavingLabel: false }, () => {
                    if (this.props.onCredentialUpdated) {
                        this.props.onCredentialUpdated();
                    }
                });
            } else {
                this.setState({ isSavingLabel: false });
                toast.error(t('credential_row.edit_error'));
            }
        }).catch(() => {
            this.setState({ isSavingLabel: false });
            toast.error(t('credential_row.edit_error'));
        });
    }

    handleEditKeyDown(e) {
        if (e.key === 'Enter') {
            e.preventDefault();
            this.handleSaveLabel();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            this.handleCancelEdit();
        }
    }

    handleRemove() {
        this.props.onRemove(this.props.credential.id);
    }

    handleReconnect() {
        const { credential } = this.props;
        const channel = this.props.channel;

        // Guardar contexto para que el flujo OAuth pueda etiquetar la nueva credencial.
        // En Meta el nombre no se toca al reconectar (lo elige la agencia, no el perfil con el que se
        // re-autoriza): la conexión a reconectar viaja por `reconnect_acc_id`, no por la etiqueta.
        // Escalafy tampoco: la reconexión la identifica el backend por `reconnect_credential_id`.
        if (credential.label && channel !== 'FACEBOOK' && channel !== 'LINKEDIN' && channel !== ESCALAFY
            && channel !== MERCADO_ADS) {
            localStorage.setItem('pending_credential_label', credential.label);
        }

        // Google, TikTok y DV360: la conexión viaja por su id, no sólo por el nombre (mismo mecanismo
        // que `reconnectCredential`, que es la copia de la 2.1).
        rememberCredentialReconnect(channel, credential);

        this.setState({ isReconnecting: true });

        if (channel === ESCALAFY) {
            // `credential_id` es la `EscalafyCredentials` (la que reconecta el OAuth de Escalafy); `id`, la
            // credencial genérica de la fila.
            const failed = (response = null) => {
                this.setState({ isReconnecting: false });
                toast.error(this.props.t(escalafyConnectErrorKey(response)));
            };
            startEscalafyAgencyOAuth({ reconnectCredentialId: credential.credential_id })
                .then((result) => { if (!result.ok) failed(result.response); })
                .catch(() => failed());
            return;
        }

        if (channel === MERCADO_ADS) {
            // La conexión la reconoce el backend por el usuario de Mercado Libre que autoriza: no lleva
            // nombre pendiente. Si no se puede ir, el aviso dice por qué (nunca a `undefined`).
            startMercadoAdsAgencyAuth().then((result) => {
                if (result.ok) return;
                this.setState({ isReconnecting: false });
                toast.error(this.props.t(errorViewOf(result.code).textKey));
            });
            return;
        }

        const OAUTH_AUTH_URL_BY_CHANNEL = {
            GOOGLE: 'get_auth_url/',
            ANALYTICS: 'get_auth_url/ga4/',
            SEARCH_CONSOLE: 'get_auth_url/gsc/',
            DV360: 'get_auth_url/dv360/',
        };

        if (OAUTH_AUTH_URL_BY_CHANNEL[channel]) {
            new APIClient(API_URL + OAUTH_AUTH_URL_BY_CHANNEL[channel]).call('GET')
                .then((data) => { window.location = data.auth_url; })
                .catch(() => { this.setState({ isReconnecting: false }); });
        } else if (channel === 'TIKTOK') {
            const redirectUri = encodeURIComponent(`${APP_URL}connect-tiktok`);
            window.location = `https://business-api.tiktok.com/portal/auth?app_id=${TIKTOK_APP_ID}&state=your_custom_params&redirect_uri=${redirectUri}`;
        } else if (channel === 'LINKEDIN') {
            // Reconecta ESTA conexión aunque quien autorice sea otra persona (el label solo no alcanza).
            setPendingLinkedinReconnect(credential.id, credential.label);
            window.location = LINKEDIN_AUTHORIZATION_URL;
        } else if (channel === 'FACEBOOK') {
            window.location = `/agency/conections?reconnect_pending=FACEBOOK&reconnect_acc_id=${credential.id}`;
        }
    }

    formatDate(dateString) {
        if (!dateString) return '-';
        const date = new Date(dateString);
        const { i18n } = this.props;
        return date.toLocaleString(i18n.language, {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    }

    renderDisconnectedExtras() {
        const { credential, t } = this.props;
        const since = this.formatDate(credential.disconnected_since);
        const mercadoAdsCut = this.props.channel === MERCADO_ADS
            ? t('connections.mercado_ads.disconnected', {
                nickname: mercadoAdsConnectionName(credential) || t('credential_row.unknown_label'),
                interpolation: { escapeValue: false },
            })
            : null;
        return (
            <>
                <span style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    marginLeft: '0.5em',
                    background: '#DF0D00',
                    color: '#fff',
                    borderRadius: '4px',
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    verticalAlign: 'middle',
                }}>
                    {t('credential_row.disconnected_chip')}
                </span>
                {mercadoAdsCut && (
                    <div className="credential-row-mercado-ads-cut" style={{ ...META_STYLE, color: EXPIRED_COLOR, fontWeight: 600 }}>
                        {mercadoAdsCut}
                    </div>
                )}
                <div style={{ marginTop: '0.25em', fontSize: '0.8125rem', color: '#6B7280' }}>
                    {t('credential_row.disconnected_since_meta', { date: since })}
                    {credential.ad_accounts_count > 0 && (
                        <span> · {this.props.channel === ESCALAFY
                            ? t('agency_connections.escalafy_orgs', { count: credential.ad_accounts_count })
                            : t('credential_row.affected_accounts_meta', { count: credential.ad_accounts_count })}</span>
                    )}
                </div>
            </>
        );
    }

    // Quién la conectó, sólo si el backend lo sabe: los canales que no lo estampan mandan `null`.
    renderCreatedBy() {
        const { credential, t } = this.props;
        if (!credential.created_by_user_name) return null;
        return (
            <div className="credential-row-created-by" style={META_STYLE}>
                {t('agency_connections.connected_by', {
                    name: credential.created_by_user_name, interpolation: { escapeValue: false },
                })}
            </div>
        );
    }

    renderAccountsCount() {
        const { credential, channel, t } = this.props;
        // Escalafy ve organizaciones, no cuentas publicitarias.
        if (channel === ESCALAFY) {
            return t('agency_connections.escalafy_orgs', { count: credential.ad_accounts_count || 0 });
        }
        return credential.ad_accounts_count;
    }

    expiryState() {
        const { credential, channel } = this.props;
        // Sólo LinkedIn tiene vencimiento hoy, y el copy lo nombra: el componente no depende de que el
        // backend nunca mande `expires_at` en otro canal.
        if (channel !== 'LINKEDIN') return null;
        // Una conexión creada desde el Portal la gestiona el cliente: la agencia no puede reconectarla.
        if (credential.managed_by_client) return null;
        if (credential.auto_renews) {
            return credential.status === STATUS_DISCONNECTED ? null : 'auto_renews';
        }
        if (!credential.expires_at) return null;
        const msLeft = new Date(credential.expires_at).getTime() - Date.now();
        if (msLeft <= 0) return 'expired';
        if (msLeft <= EXPIRY_WARNING_MS) return 'expiring';
        return null;
    }

    renderExpiryMeta(expiryState) {
        const { credential, t, i18n } = this.props;
        if (!expiryState) return null;
        if (expiryState === 'auto_renews') {
            return (
                <div style={{ marginTop: '0.25em', fontSize: '0.8125rem', color: '#6B7280' }}>
                    {t('credential_row.auto_renews')}
                </div>
            );
        }
        // En hora de Argentina, igual que el mail que avisa el vencimiento.
        const date = new Date(credential.expires_at).toLocaleDateString(i18n.language, {
            day: '2-digit', month: 'short', year: 'numeric', timeZone: 'America/Argentina/Buenos_Aires',
        });
        const key = expiryState === 'expired' ? 'credential_row.expired_on' : 'credential_row.expires_on';
        return (
            <div className="credential-row-expiry"
                 style={{
                     marginTop: '0.25em', fontSize: '0.8125rem', fontWeight: 600,
                     color: expiryState === 'expired' ? EXPIRED_COLOR : EXPIRING_COLOR,
                 }}>
                {t(key, { date, interpolation: { escapeValue: false } })}
            </div>
        );
    }

    render() {
        const { credential, t, i18n } = this.props;
        // Mercado Ads nace con el id numérico del vendedor como nombre: se muestra su apodo.
        const label = (this.props.channel === MERCADO_ADS ? mercadoAdsConnectionName(credential) : credential.label)
            || t('credential_row.unknown_label');
        const createdAt = credential.created_at
            ? new Date(credential.created_at).toLocaleDateString(i18n.language)
            : '-';
        const isDisconnected = credential.status === STATUS_DISCONNECTED;
        const expiryState = this.expiryState();
        // Una conexión de Escalafy que hizo el cliente desde el portal la reconecta él: el OAuth de la agencia
        // crearía otra credencial, de la agencia, con el token del dueño.
        // Mercado Ads, igual: lo que conectó el cliente (Portal o link) se le vuelve a pedir con un link nuevo.
        const managedByClientChannel = this.props.channel === ESCALAFY || this.props.channel === MERCADO_ADS;
        const reconnectableByAgency = !(managedByClientChannel && credential.managed_by_client);
        const needsReconnect = reconnectableByAgency
            && (isDisconnected || expiryState === 'expiring' || expiryState === 'expired');
        const sendNewLink = this.props.channel === MERCADO_ADS && isDisconnected && credential.managed_by_client
            && this.props.onSendLink;

        const rowStyle = isDisconnected ? {
            background: '#FEF2F2',
            borderLeft: '4px solid #DF0D00',
        } : {};

        const { isEditing, editValue, isSavingLabel } = this.state;

        return (
            <tr style={rowStyle}>
                <td>
                    {isEditing ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4em' }}>
                            <input
                                className="form-control"
                                type="text"
                                value={editValue}
                                maxLength={255}
                                autoFocus
                                disabled={isSavingLabel}
                                placeholder={t('credential_row.edit_placeholder')}
                                onChange={(e) => this.setState({ editValue: e.target.value })}
                                onKeyDown={this.handleEditKeyDown}
                                style={{ width: '14em', display: 'inline-block' }}
                            />
                            <button type="button"
                                    className="btn -alt"
                                    onClick={this.handleSaveLabel}
                                    disabled={isSavingLabel}
                                    title={t('credential_row.edit_save_title')}>
                                <FaIcon iconName="check"/>
                            </button>
                            <button type="button"
                                    className="btn -alt -negative"
                                    onClick={this.handleCancelEdit}
                                    disabled={isSavingLabel}
                                    title={t('credential_row.edit_cancel_title')}>
                                <FaIcon iconName="times"/>
                            </button>
                        </span>
                    ) : (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5em' }}>
                            <span>{label}</span>
                            {this.props.credential && this.props.credential.managed_by_client &&
                                <ManagedByClientTag clientName={this.props.credential.managed_by_client_name}/>}
                            <button type="button"
                                    onClick={this.handleStartEdit}
                                    title={t('credential_row.edit_button_title')}
                                    style={{
                                        background: 'transparent',
                                        border: 'none',
                                        padding: '0 0.25em',
                                        cursor: 'pointer',
                                        color: '#6B7280',
                                    }}>
                                <FaIcon iconName="pencil-alt"/>
                            </button>
                        </span>
                    )}
                    {this.renderCreatedBy()}
                    {isDisconnected && this.renderDisconnectedExtras()}
                    {this.renderExpiryMeta(expiryState)}
                </td>
                <td>
                    {credential.is_syncing ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5em', color: '#6B7280' }}>
                            <Spinner color="positive small"/>
                            {t('credential_row.syncing')}
                        </span>
                    ) : (
                        this.renderAccountsCount()
                    )}
                </td>
                <td>{createdAt}</td>
                <td>
                    {/* Botón y tacho en una fila (el tacho caía debajo del botón y las filas quedaban
                        desparejas — QA-VIS-linkedin-04). */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em', flexWrap: 'nowrap' }}>
                    {sendNewLink && (
                        <button className="btn -alt"
                                style={{ whiteSpace: 'nowrap' }}
                                data-testid="credential-row-send-link"
                                onClick={() => this.props.onSendLink(credential)}>
                            {t('connections.mercado_ads.send_new_link')}
                        </button>
                    )}
                    {needsReconnect && (
                        <button className="btn -alt"
                                style={{ whiteSpace: 'nowrap' }}
                                onClick={this.handleReconnect}
                                disabled={this.state.isReconnecting}
                                title={t('credential_row.reconnect_button_title')}>
                            <FaIcon iconName="sync-alt"/> {t('credential_row.reconnect_button')}
                        </button>
                    )}
                    <YesNoModal onYesAnswer={this.handleRemove}
                                title={t('credential_row.delete_title')}
                                message={t(this.props.channel === ESCALAFY
                                    ? 'agency_connections.escalafy_delete_confirm'
                                    : 'credential_row.delete_message')}
                                question={t('credential_row.delete_question')}
                                buttonClassName={""}
                                buttonText={""}
                                buttonTitle={t('credential_row.delete_button_title')}
                                buttonIconName={"trash"}
                                isSaving={this.props.isSaving}
                    />
                    </div>
                </td>
            </tr>
        );
    }
}

export const CredentialRow = withTranslation()(CredentialRowClass);
export default CredentialRow;
